import { Suspense, useMemo } from 'react';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CloseIcon from '@atlaskit/icon/core/cross';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { SMARTLINK_CONNECTION_TOUCHPOINTS } from '@atlassian/confluence-account-setup/entry-points/smartlink-connection-analytics';
import {
	type AnimationApp,
	type Department,
	getAnimationSvg,
} from '@atlassian/confluence-account-setup/inline-connection-screen/get-animation-svg';
import { SplitScreen } from '@atlassian/confluence-account-setup/split-screen';
import { ThirdPartyConnectorList } from '@atlassian/confluence-account-setup/third-party-connector-list';
import type { ConnectorInfo } from '@atlassian/confluence-account-setup/use3p-connector-resources';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { type JSX } from 'react/jsx-runtime';

export type { AnimationApp, Department };

// Mirrors the desktop height used by the platform account-setup shell that this
// split-screen modal experience is based on.
const ACCOUNT_SETUP_MODAL_DESKTOP_HEIGHT = '665px';

const styles = cssMap({
	closeButtonContainer: {
		position: 'absolute',
		right: token('space.200'),
		top: token('space.200'),
	},
	modalBody: {
		borderRadius: 'inherit',
		overflow: 'hidden',

		'@media (min-width: 48rem)': {
			height: ACCOUNT_SETUP_MODAL_DESKTOP_HEIGHT,
		},
	},
	dynamicComponentContainer: {
		borderStartEndRadius: 'inherit',
		borderEndEndRadius: 'inherit',
		height: '100%',
		overflow: 'hidden',
		position: 'relative',
	},
});

const i18n = defineMessages({
	modalTitle: {
		id: 'post-office.confluence-connect-3p-tools-nudge.modal.title',
		defaultMessage: 'Connect your most-used apps',
		description: 'Title of the modal that allows users to connect 3rd party tools.',
	},
	modalSubtitle: {
		id: 'post-office.confluence-connect-3p-tools-nudge.modal.subtitle',
		defaultMessage:
			'<strong>Bring your team\u2019s work</strong> into Confluence pages with rich, interactive previews \u2013 no tab-switching needed.',
		description: 'Subtitle of the modal that allows users to connect 3rd party tools.',
	},
	modalTeammatesEmpty: {
		id: 'post-office.confluence-connect-3p-tools-nudge.modal.teammates.empty',
		defaultMessage: 'Teams stay in sync by seeing live updates directly in Confluence.',
		description:
			'Shown below the connector list when no teammates have connected an app (0 teammates).',
	},
	modalTeammatesFew: {
		id: 'post-office.confluence-connect-3p-tools-nudge.modal.teammates.few',
		defaultMessage: 'Your team is already using {appName} to work smarter.',
		description: 'Shown below the connector list when 1-20 teammates have connected the app.',
	},
	modalTeammatesMany: {
		id: 'post-office.confluence-connect-3p-tools-nudge.modal.teammates.many',
		defaultMessage:
			'\uD83D\uDE4C <strong>{numTeammates} teammates</strong> already connected {appName} to Confluence.',
		description: 'Shown below the connector list when 21-50 teammates have connected the app.',
	},
	modalTeammatesLots: {
		id: 'post-office.confluence-connect-3p-tools-nudge.modal.teammates.lots',
		defaultMessage:
			'\uD83D\uDE4C <strong>50+ teammates</strong> already connected {appName} to Confluence.',
		description:
			'Shown below the connector list when more than 50 teammates have connected the app.',
	},
	modalClose: {
		id: 'post-office.confluence-connect-3p-tools-nudge.modal.close',
		defaultMessage: 'Close',
		description: 'Close button in the 3p tools modal.',
	},
	modalDismiss: {
		id: 'post-office.confluence-connect-3p-tools-nudge.modal.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Dismiss button in the 3p tools modal footer.',
	},
});

type TeammatesProps =
	| { numTeammates?: undefined; appName?: undefined }
	| { numTeammates: number; appName: string };

export type Connect3pToolsModalProps = {
	isOpen: boolean;
	onClose: () => void;
	connectorResources: ConnectorInfo[];
	onConnectorConnected?: (productKey: string) => void;
	/** The user's top 3 recommended apps in priority order — used to select the animation SVG. */
	topApps?: AnimationApp[];
	/** The user's department — used as fallback to select the animation SVG. */
	department?: Department;
} & TeammatesProps;

export const Connect3pToolsModal = ({
	isOpen,
	onClose,
	connectorResources,
	onConnectorConnected,
	numTeammates,
	appName,
	topApps,
	department,
}: Connect3pToolsModalProps): JSX.Element => {
	const intl = useIntl();
	const AnimationSvg = useMemo(
		() => getAnimationSvg({ topApps, department }),
		[topApps, department],
	);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal
					onClose={onClose}
					testId="confluence-connect-3p-tools-modal"
					width="x-large"
					label={intl.formatMessage(i18n.modalTitle)}
					shouldCloseOnOverlayClick={false}
				>
					<Box xcss={styles.modalBody}>
						<SplitScreen
							screen="confluence-connect-3p-tools-modal"
							title={intl.formatMessage(i18n.modalTitle)}
							subtitle={
								<FormattedMessage
									{...i18n.modalSubtitle}
									values={{ strong: (chunks) => <strong>{chunks}</strong> }}
								/>
							}
							hideLogo
							dynamicComponent={
								<Box
									backgroundColor="color.background.accent.blue.subtlest.hovered"
									xcss={styles.dynamicComponentContainer}
								>
									<Box xcss={styles.closeButtonContainer}>
										<IconButton
											appearance="subtle"
											icon={CloseIcon}
											label={intl.formatMessage(i18n.modalClose)}
											onClick={onClose}
											spacing="compact"
											testId="confluence-connect-3p-tools-modal-close-button"
										/>
									</Box>
									<Suspense fallback={<Box />}>
										<AnimationSvg />
									</Suspense>
								</Box>
							}
							imageAlt=""
							footer={
								<Flex justifyContent="start" alignItems="center">
									<Button appearance="subtle" onClick={onClose}>
										<FormattedMessage {...i18n.modalDismiss} />
									</Button>
								</Flex>
							}
						>
							<Box>
								<ThirdPartyConnectorList
									connectorResources={connectorResources}
									onConnectorConnected={onConnectorConnected}
									showIconTile
									touchpoint={SMARTLINK_CONNECTION_TOUCHPOINTS.sideNav}
									screenName="confluence-connect-3p-tools-modal"
								/>
								{numTeammates !== undefined && (
									<Box paddingBlockStart="space.200">
										<Text size="small" color="color.text.subtle">
											{numTeammates === 0 && <FormattedMessage {...i18n.modalTeammatesEmpty} />}
											{numTeammates >= 1 && numTeammates <= 20 && (
												<FormattedMessage {...i18n.modalTeammatesFew} values={{ appName }} />
											)}
											{numTeammates >= 21 && numTeammates <= 50 && (
												<FormattedMessage
													{...i18n.modalTeammatesMany}
													values={{
														numTeammates,
														appName,
														strong: (chunks) => <strong>{chunks}</strong>,
													}}
												/>
											)}
											{numTeammates > 50 && (
												<FormattedMessage
													{...i18n.modalTeammatesLots}
													values={{
														appName,
														strong: (chunks) => <strong>{chunks}</strong>,
													}}
												/>
											)}
										</Text>
									</Box>
								)}
							</Box>
						</SplitScreen>
					</Box>
				</Modal>
			)}
		</ModalTransition>
	);
};
